import { action, makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "./root";

export interface QABreakdown {
  valid: number;
  invalid: number;
  "not-checked": number;
}

export interface OverallMetrics {
  total_interviews: number;
  unique_enumerators: number;
  qa_breakdown: QABreakdown;
  quality: number;
  qc_backlog: number;
  status_line: string;
}

export interface ProjectSummary {
  project_id: number;
  project_name: string;
  total_interviews: number;
  unique_enumerators: number;
  qa_breakdown: QABreakdown;
  quality: number;
  qc_backlog: number;
  status_line: string;
}

export interface CockpitSummaryResponse {
  overall: OverallMetrics;
  projects: ProjectSummary[];
}

export interface TimelineEntry {
  date: string;
  total_interviews: number;
  unique_enumerators: number;
  qa_breakdown: QABreakdown;
  quality: number;
  qc_backlog: number;
  status_line: string;
}

export class CockpitStore {
  rootStore: RootStore;
  
  // Summary data
  summaryData: CockpitSummaryResponse | null = null;
  timelineData: TimelineEntry[] = [];
  
  // Loading states
  isLoadingSummary: boolean = false;
  isLoadingTimeline: boolean = false;
  
  // Filters
  currentPeriod: string = "all_time";
  days: number = 60;
  minResponses: number = 5;
  customStart: string | null = null;
  customEnd: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  @action
  setCurrentPeriod(period: string) {
    this.currentPeriod = period;
  }

  @action
  setDays(days: number) {
    this.days = days;
  }

  @action
  setMinResponses(minResponses: number) {
    this.minResponses = minResponses;
  }

  @action
  setCustomDateRange(start: string | null, end: string | null) {
    this.customStart = start;
    this.customEnd = end;
  }

  @action
  async fetchCockpitSummary(
    days?: number,
    minResponses?: number,
    period?: string,
    customStart?: string | null,
    customEnd?: string | null
  ) {
    this.isLoadingSummary = true;
    
    const payload: any = {
      days: days ?? this.days,
      min_responses: minResponses ?? this.minResponses,
      period: period ?? this.currentPeriod,
    };

    if (customStart) payload.custom_start = customStart;
    if (customEnd) payload.custom_end = customEnd;

    try {
      const response = await this.rootStore.api?.post("cockpit/summary", payload);
      
      if (response?.ok && response.data) {
        runInAction(() => {
          this.summaryData = response.data;
          this.isLoadingSummary = false;
        });
        return response.data;
      } else {
        runInAction(() => {
          this.isLoadingSummary = false;
        });
        return null;
      }
    } catch (error) {
      console.error("Error fetching cockpit summary:", error);
      runInAction(() => {
        this.isLoadingSummary = false;
      });
      return null;
    }
  }

  @action
  async fetchCockpitTimeline(
    days?: number,
    minResponses?: number,
    period?: string,
    customStart?: string | null,
    customEnd?: string | null
  ) {
    this.isLoadingTimeline = true;
    
    const payload: any = {
      days: days ?? this.days,
      min_responses: minResponses ?? this.minResponses,
      period: period ?? this.currentPeriod,
    };

    if (customStart) payload.custom_start = customStart;
    if (customEnd) payload.custom_end = customEnd;

    try {
      const response = await this.rootStore.api?.post("cockpit/timeline", payload);
      
      if (response?.ok && response.data) {
        runInAction(() => {
          this.timelineData = response.data;
          this.isLoadingTimeline = false;
        });
        return response.data;
      } else {
        runInAction(() => {
          this.isLoadingTimeline = false;
        });
        return null;
      }
    } catch (error) {
      console.error("Error fetching cockpit timeline:", error);
      runInAction(() => {
        this.isLoadingTimeline = false;
      });
      return null;
    }
  }

  @action
  async fetchProjectDetails(projectId: number, period?: string, customStart?: string | null, customEnd?: string | null) {
    const payload: any = {
      projectid: projectId,
      period: period ?? this.currentPeriod,
    };

    if (customStart) payload.custom_start = customStart;
    if (customEnd) payload.custom_end = customEnd;

    try {
      const response = await this.rootStore.api?.post("cockpit/project_details", payload);
      
      if (response?.ok && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching project details:", error);
      return null;
    }
  }

  @action
  reset() {
    this.summaryData = null;
    this.timelineData = [];
    this.currentPeriod = "all_time";
    this.days = 60;
    this.minResponses = 5;
    this.customStart = null;
    this.customEnd = null;
  }
}

